import { useEffect, useState } from 'react';
import { api } from './api.js';

interface Contributor { code: string; label: string; points: number; detail: string; actionable: boolean }
interface Score { score: number; band: 'low' | 'watch' | 'elevated' | 'act'; contributors: Contributor[]; staleReason: string | null }
interface RiskRow {
  id: string; effect: string; plain_summary_md: string; onset: string | null;
  reversibility: string | null; tracked_by_sign_md: string | null; marker_label: string | null;
  managed_with_class: string | null; managed_with_note_md: string | null;
  requires_prescription: boolean; non_drug_mitigation_md: string | null;
  decision_window_md: string | null; decision_window_urgency: string; severity: number;
}
interface Profile { compounds: string[]; risks: RiskRow[]; decisionWindows: RiskRow[] }

const BAND_LABEL: Record<string, string> = { low: 'Low', watch: 'Watch', elevated: 'Elevated', act: 'See a doctor' };

export function Risk(): JSX.Element {
  const [score, setScore] = useState<Score | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void api<Score>('/risk/score').then(setScore).catch((e: Error) => setErr(e.message));
    void api<Profile>('/risk/profile').then(setProfile).catch(() => undefined);
  }, []);

  if (err) return <div className="card"><p className="bad">{err}</p></div>;

  return (
    <>
      {score && (
        <div className={`card band-${score.band}`}>
          <h2>Risk score</h2>
          <p className="scoreline"><span className="bignum">{score.score}</span> <span className={`band band-${score.band}`}>{BAND_LABEL[score.band]}</span></p>
          {score.staleReason
            ? <p className="muted small">{score.staleReason}</p>
            : <p className="muted small">Computed only from your own measurements. It is not an opinion about what you are running.</p>}
          {score.contributors.length === 0 && !score.staleReason && <p className="muted small">Nothing is contributing to your score right now.</p>}
          <ul className="contrib">
            {score.contributors.map((c) => (
              <li key={c.code} className={c.actionable ? 'actionable' : ''}>
                <div className="crow"><strong>{c.label}</strong><span className="pts">+{c.points}</span></div>
                <p className="muted small">{c.detail}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {profile && profile.decisionWindows.length > 0 && (
        <div className="card warn">
          <h2>Decision windows</h2>
          <p className="muted small">These are the ones where waiting costs you options.</p>
          {profile.decisionWindows.map((r) => (
            <div key={r.id} className="subcard">
              <strong>{r.effect}</strong>{' '}
              <span className={`chip chip-${r.decision_window_urgency === 'closing' ? 'low' : 'medium'}`}>
                {r.decision_window_urgency === 'closing' ? 'closing' : 'may already have passed'}
              </span>
              <p className="small">{r.decision_window_md}</p>
            </div>
          ))}
        </div>
      )}

      {profile && (
        <div className="card">
          <h2>What you are running, and what it does</h2>
          {profile.compounds.length === 0 && <p className="muted">Nothing entered. Add it in the setup guide if you want this filled in.</p>}
          {profile.risks.map((r) => (
            <details key={r.id} className="risk">
              <summary>
                <span className={`sev sev-${r.severity}`} />{r.effect}
                {r.marker_label && <span className="muted small"> · tracked by {r.marker_label}</span>}
              </summary>
              <p className="small">{r.plain_summary_md}</p>
              {r.tracked_by_sign_md && <p className="small"><strong>How you see it:</strong> {r.tracked_by_sign_md}</p>}
              {r.non_drug_mitigation_md && <p className="small"><strong>What helps without a prescription:</strong> {r.non_drug_mitigation_md}</p>}
              {r.managed_with_class && (
                <p className="small">
                  <strong>Managed clinically with:</strong> {r.managed_with_class}
                  {r.requires_prescription ? ' (prescription medicine, needs a doctor)' : ''}
                  {r.managed_with_note_md ? ` ${r.managed_with_note_md}` : ''}
                </p>
              )}
              {r.onset && <p className="muted small">Onset: {r.onset} · Reversibility: {r.reversibility}</p>}
            </details>
          ))}
        </div>
      )}
    </>
  );
}
